import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
  Paper,
  Modal,
  FormControl,
  InputLabel,
  TextField,
  Box,
  Grid,
  Checkbox,
  ListItemText,
  MenuItem,
  Select,
  Pagination,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useParams } from "react-router-dom";
import base64 from "base64-js";
import LocationOnIcon from "@mui/icons-material/LocationOn";


const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});


function EnquiryDetails() {
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [enqDetails, setEnqDetails] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loadingBarProgress, setLoadingBarProgress] = useState("");
  const [selectedEnquiryId, setSelectedEnquiryId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [isMaster, setIsMaster] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(1);

  // Info modal state
  const [showInfoModal, setShowInfoModal] = useState(false);

  const navigate = useNavigate();

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const { encodedFormId } = useParams();
  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId, }), // replace with actual formId if hardcoded
      });

      const result = await response.json();
      const data = result[0];

      // setAuthorization(data);
      setCanEdit(data?.uModify === 1);
      setCanAdd(data?.uWrite === 1);
      setCanDelete(data?.uDelete === 1);
    } catch (error) {
      console.error("Authorization check failed:", error);
    }
  };


  const statusOptions = [
    "Super Hot",
    "Very Hot",
    "Hot",
    "Cold",
    "Very Cold",
    "Lost",
    "Converted",
  ];

  const fetchBranches = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      } else {
        console.error("Failed to fetch branches");
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchEnquiry = async () => {
    try {
      setIsLoading(true);
      const token = sessionStorage.getItem("token");
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");
      const response = await fetch(`${apiUrl}/Enquiry/FetchSchoolEnquiry?Branch_Id=${branchId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          status: selectedStatuses.length > 0 ? selectedStatuses : null,
        }),
      });
      if (!response.ok) {
        throw new Error(`Error fetching enquiries: ${response.status}`);
      }
      const data = await response.json();
      const enquiryArray = Array.isArray(data) ? data : [];
      setEnqDetails(enquiryArray);
    } catch (error) {
      console.error("Fetch Error:", error);
      setEnqDetails([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBranchId) {
      fetchEnquiry();
    }
  }, [selectedBranchId]);

  useEffect(() => {
    const isMasterUser = sessionStorage.getItem("is_Master") === "true";
    setIsMaster(isMasterUser);

    if (isMasterUser) {
      fetchBranches();
    } else {
      const branchId = sessionStorage.getItem("branchId");
      setSelectedBranchId(branchId);
    }

    fetchEnquiry();
    Authorizer();
  }, [selectedStatuses]);


  const handleClick = () => {
    navigate("/addenquiry");
  };

  const handleDelete = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const apiUrl = process.env.REACT_APP_BASE_URL;
      setLoadingBarProgress(30);
      const response = await fetch(
        `${apiUrl}/Enquiry/Id?EnquiryId=${selectedEnquiryId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );

      if (response.ok) {
        setLoadingBarProgress(100);
        fetchEnquiry();
        handleCloseModal();
        setSnackbarMessage('Enquiry deleted successfully!');
        setOpenSnackbar(true);
        setSnackbarSeverity("success");
      } else {
        setLoadingBarProgress(0);
        console.error("Failed to delete enquiry");
        // alert("Failed to delete enquiry");
        setSnackbarMessage('Failed to delete enquiry');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error("API request error:", error);
      // alert("An error occurred. Please try again later.");
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  const handleShowModal = (enquiryId) => {
    setSelectedEnquiryId(enquiryId);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEnquiryId(null);
  };

  const filteredEnqDetails = Array.isArray(enqDetails)
    ? enqDetails.filter((enquiry) =>
      enquiry.studentName?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : [];

  // Pagination logic
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page when rows per page changes
  };

  const paginatedEnqDetails = filteredEnqDetails.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Info sections data
  const infoSections = [
    {
      title: "📋 Page Overview",
      content:
        "The Enquiry Details page displays all school admission enquiries. It allows staff to view, search, filter, add, edit, and delete student enquiry records based on their access permissions.",
    },
    {
      title: "🔍 Search & Filter",
      items: [
        "Search by Student Name: Type in the search box to instantly filter enquiries by the student's name.",
        "Filter by Status: Use the multi-select dropdown to filter enquiries by one or more statuses (e.g., Hot, Cold, Converted, Lost, etc.).",
      ],
    },
    {
      title: "📊 Table Columns",
      items: [
        "Enquiry No. – Unique code assigned to each enquiry.",
        "Enquiry Date – The date the enquiry was submitted.",
        "Student Name – Name of the prospective student.",
        "Father Name – Name of the parent or guardian.",
        "Email-Id – Contact email address.",
        "Contact No. – Phone number for follow-up.",
        "Class – The class/grade the student is enquiring for.",
        "Status – Current lead status (Super Hot, Hot, Cold, Converted, Lost, etc.).",
        "Action – Edit or Delete buttons based on your access rights.",
      ],
    },
    {
      title: "🏷️ Enquiry Statuses",
      items: [
        "Super Hot – Very high interest, likely to convert soon.",
        "Very Hot – High interest, follow up promptly.",
        "Hot – Interested, needs nurturing.",
        "Cold – Low interest or unresponsive.",
        "Very Cold – Minimal interest.",
        "Lost – Enquiry did not convert.",
        "Converted – Successfully admitted as a student.",
      ],
    },
    {
      title: "⚙️ Actions",
      items: [
        "Add Enquiry – Opens the form to record a new student enquiry. Requires Add permission.",
        "Edit – Modify an existing enquiry record. Requires Edit permission.",
        "Delete – Permanently removes the enquiry after confirmation. Requires Delete permission.",
      ],
    },
    {
      title: "🏢 Branch Selection (Master Users Only)",
      content:
        "If you are a master/admin user, a Branch dropdown appears in the top-right of the header. Selecting a branch will load enquiries specific to that branch.",
    },
    {
      title: "📄 Pagination",
      content:
        "Use the Rows per page selector to control how many records are shown at once (5, 10, 25, or 50). Navigate between pages using the pagination controls at the bottom.",
    },
  ];

  return (
    <Container maxWidth={false} style={{ width: '100%' }}>
      <AppBar
        position="static"
        sx={{
          backgroundColor: "#0B1F3D",
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3 }}>

          {/* Left: Title + Info Button */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                letterSpacing: "0.5px",
                color: "#ffffff",
              }}
            >
              Enquiry Details
            </Typography>

            {/* Info Icon Button */}
            <Tooltip title="View page information & guide" arrow>
              <IconButton
                onClick={() => setShowInfoModal(true)}
                size="small"
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  border: "1.5px solid rgba(255,255,255,0.25)",
                  borderRadius: "50%",
                  width: 30,
                  height: 30,
                  ml: 0.5,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(100,181,246,0.25)",
                    borderColor: "#64B5F6",
                    color: "#64B5F6",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <InfoIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Right: Branch Dropdown */}
          {isMaster && (
            <Box sx={{ position: "relative" }}>
              <FormControl size="small" sx={{ width: 220 }}>
                <InputLabel
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                    top: "-2px",
                    "&.Mui-focused": { color: "#64B5F6" },
                    "&.MuiFormLabel-filled": { color: "rgba(255,255,255,0.7)" },
                  }}
                >
                  Branch
                </InputLabel>
                <Select
                  value={selectedBranchId || ""}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                  label="Branch"
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.5)", ml: 0.5 }} />
                    </InputAdornment>
                  }
                  sx={{
                    color: "#ffffff",
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    letterSpacing: "0.3px",
                    backgroundColor: "rgba(255,255,255,0.07)",
                    backdropFilter: "blur(8px)",
                    borderRadius: "8px",
                    transition: "background-color 0.2s ease, box-shadow 0.2s ease",

                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.15)",
                      borderWidth: "1px",
                      transition: "border-color 0.2s ease",
                    },
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.11)",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(255,255,255,0.35)",
                      },
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                      boxShadow: "0 0 0 3px rgba(100,181,246,0.18)",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#64B5F6",
                        borderWidth: "1.5px",
                      },
                    },
                    "& .MuiSelect-icon": {
                      color: "rgba(255,255,255,0.5)",
                      transition: "color 0.2s ease",
                    },
                    "&.Mui-focused .MuiSelect-icon": {
                      color: "#64B5F6",
                    },
                    "& .MuiSelect-select": {
                      py: 1.1,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: "10px",
                        border: "1px solid rgba(11,31,61,0.12)",
                        boxShadow: "0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)",
                        overflow: "hidden",
                        "& .MuiList-root": { py: 0.5 },
                      },
                    },
                  }}
                >
                  {branches.map((branch) => (
                    <MenuItem
                      key={branch.branchId}
                      value={branch.branchId}
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        color: "#0B1F3D",
                        px: 2,
                        py: 1.1,
                        mx: 0.5,
                        borderRadius: "6px",
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: "rgba(11,31,61,0.07)",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "rgba(25,118,210,0.1)",
                          color: "#1565C0",
                          fontWeight: 600,
                          "&:hover": {
                            backgroundColor: "rgba(25,118,210,0.15)",
                          },
                        },
                      }}
                    >
                      {branch.branchName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Paper
        elevation={3}
        style={{ padding: 16, width: "100%", margin: "auto", marginTop: 6 }}
      >
        {/* Search, Status Dropdown, and Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}
        >
          {/* Left side: Filters */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              flex: 1,
              minWidth: 0,
            }}
          >
            {/* Search */}
            <TextField
              label="Search by Student Name"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                width: "260px",
                "& .MuiInputLabel-outlined": {
                  transform: "translate(14px, 12px) scale(1)",
                  "&.MuiInputLabel-shrink": {
                    transform: "translate(14px, -6px) scale(0.75)",
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Status Filter */}
            <FormControl sx={{ width: "200px" }}>
              <InputLabel id="status-label">Filter by Status</InputLabel>
              <Select
                labelId="status-label"
                label="Filter by Status"
                multiple
                value={selectedStatuses}
                onChange={(e) => setSelectedStatuses(e.target.value)}
                renderValue={(selected) => selected.join(", ")}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 300 } },
                }}
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={selectedStatuses.indexOf(status) > -1} />
                    <ListItemText primary={status} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Right side: Add Button */}
          <Tooltip title={!canAdd ? "You are not Authorized to Add" : ""} arrow>
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={handleClick}
                disabled={!canAdd}
                sx={{
                  textTransform: "none",
                  width: "130px",
                  height: "48px",
                  fontWeight: "bold",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                Add Enquiry
              </Button>
            </span>
          </Tooltip>
        </Box>


        {/* Scrollable Table */}
        <Box sx={{ maxHeight: 400, overflow: "auto" }}>
          <TableContainer component={Paper}>
            <Table
              sx={{
                minWidth: 650,
                border: '1px solid #ccc',
                '& .MuiTableCell-root': {
                  borderRight: '1px solid #ccc', // vertical line
                  fontSize: '1rem', // increased font size
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    <strong>Enquiry No.</strong>
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    <strong>Enquiry Date</strong>
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    <strong>Student Name</strong>
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    <strong>Father Name</strong>
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    <strong>Email-Id</strong>
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    <strong>Contact No.</strong>
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    <strong>Class</strong>
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    <strong>Status</strong>
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'center' }}>
                    <strong>Action</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <CircularProgress size={24} />
                        <Typography>Loading Enquiries...</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : paginatedEnqDetails.length > 0 ? (
                  paginatedEnqDetails.map((enquiry, index) => (
                    <TableRow key={enquiry.enquiryId} sx={{ borderTop: '1px solid #ccc' }}>
                      <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>{enquiry.enquiryCode || "N/A"}</TableCell>
                      <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>{enquiry.enquiryDate || "N/A"}</TableCell>
                      <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>{enquiry.studentName || "N/A"}</TableCell>
                      <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>{enquiry.parentAndGurdianName || "N/A"}</TableCell>
                      <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>{enquiry.emailId || "N/A"}</TableCell>
                      <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>{enquiry.contactNo || "N/A"}</TableCell>
                      <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>{enquiry.className || "N/A"}</TableCell>
                      <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>{enquiry.status || "N/A"}</TableCell>
                      <TableCell sx={{ textAlign: "center", borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                        <Box sx={{ display: "flex", gap: 1, justifyContent: 'center' }}>
                          <Tooltip title={!canEdit && "You are not Authorized to Edit"} arrow><span>

                            <Button
                              variant="contained"
                              color="warning"
                              size="small"
                              onClick={() => navigate(`/editenquiry/${enquiry.enquiryId}`)}
                              startIcon={<EditIcon />}
                              sx={{
                                // borderRadius: "10px",
                                padding: "6px 12px",
                                minWidth: "80px",
                              }}
                              disabled={!canEdit}
                            >
                              Edit
                            </Button></span></Tooltip>
                          <Tooltip title={!canDelete && "You are not Authorized to Delete"} arrow><span>
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={() => handleShowModal(enquiry.enquiryId)}
                              startIcon={<DeleteIcon />}
                              sx={{
                                // borderRadius: "10px",
                                padding: "6px 12px",
                                minWidth: "80px",
                              }}
                              disabled={!canDelete}
                            >
                              Delete
                            </Button></span></Tooltip>

                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No data found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>


        {/* Pagination and Rows Per Page */}
        {filteredEnqDetails.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id="rows-per-page-label">Rows per page</InputLabel>
              <Select
                labelId="rows-per-page-label"
                label="Rows per page"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Pagination
              count={Math.ceil(filteredEnqDetails.length / rowsPerPage)}
              page={page}
              onChange={handleChangePage}
              color="primary"
            />
          </Box>
        )}

        {/* Confirmation Modal */}
        <Modal open={showModal} onClose={handleCloseModal} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
              <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
                Delete Confirmation ⚠️
              </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ padding: '20px' }}>
              <Typography id="delete-modal-description" sx={{ mb: 2 }}>
                Are you sure you want to delete this Enquiry ?
              </Typography>

              {/* Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button onClick={handleCloseModal} variant="outlined" sx={{ mr: 1 }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleDelete(handleDelete)}
                >
                  Yes, Delete
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Paper>

      {/* ===== Info Modal ===== */}
      <Modal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 620 },
            maxHeight: '85vh',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 8px 40px rgba(11,31,61,0.22)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              backgroundColor: '#0B1F3D',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ color: '#64B5F6', fontSize: 22 }} />
              <Typography id="info-modal-title" variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                Enquiry Details — Page Guide
              </Typography>
            </Box>
            
          </Box>

          {/* Modal Body — scrollable */}
          <Box sx={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
            {infoSections.map((section, idx) => (
              <Box key={idx} sx={{ mb: 2.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#0B1F3D',
                    mb: 0.8,
                    fontSize: '0.97rem',
                  }}
                >
                  {section.title}
                </Typography>

                {section.content && (
                  <Typography
                    variant="body2"
                    sx={{ color: '#444', lineHeight: 1.7, fontSize: '0.875rem' }}
                  >
                    {section.content}
                  </Typography>
                )}

                {section.items && (
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {section.items.map((item, i) => (
                      <Box
                        component="li"
                        key={i}
                        sx={{
                          fontSize: '0.875rem',
                          color: '#444',
                          lineHeight: 1.75,
                          mb: 0.3,
                        }}
                      >
                        {item}
                      </Box>
                    ))}
                  </Box>
                )}

                {idx < infoSections.length - 1 && (
                  <Divider sx={{ mt: 2, borderColor: 'rgba(11,31,61,0.08)' }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              padding: '12px 24px',
              borderTop: '1px solid rgba(11,31,61,0.1)',
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: '#f9fafb',
            }}
          >
            <Button
              variant="contained"
              onClick={() => setShowInfoModal(false)}
              sx={{
                backgroundColor: '#0B1F3D',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#1a3560' },
              }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </Container>
  );
}

export default EnquiryDetails;